import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import styled, { ThemeProvider } from "styled-components/macro";
import { useQuery } from "react-query";
import ResetZoomControl from "./ResetZoomControl";
import ToggleBasemapControl from "./ToggleBasemapControl";
import debounce from "lodash.debounce";
import ReactDOM from "react-dom";
import {
  jssPreset,
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import createTheme from "../../theme";
import Popup from "../../pages/publicMap/popup";
import { create } from "jss";
import axios from "axios";
import { DEFAULT_MAP_CENTER } from "../../pages/publicMap/constants";
import Legend from "./components/Legend";
import LegendControl from "./LegendControl";

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const QueryAndDownloadMap = ({ locations }) => {
  const theme = createTheme();
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const [map, setMap] = useState();
  const [legendVisible, setLegendVisible] = useState(true);

  const popUpRef = useRef(
    new mapboxgl.Popup({
      maxWidth: "400px",
      offset: 15,
      focusAfterOpen: false,
    })
  );
  const mapContainer = useRef(null); // create a reference to the map container

  const DUMMY_BASEMAP_LAYERS = [
    { url: "streets-v11", icon: "commute" },
    { url: "outdoors-v11", icon: "park" },
    { url: "satellite-streets-v11", icon: "satellite_alt" },
  ];

  const locationsLayer = {
    id: "locations",
    type: "circle",
    source: "locations",
    paint: {
      "circle-stroke-width": 1,
      "circle-stroke-color": "black",
      "circle-radius": 7,
      "circle-color": [
        "match",
        ["get", "locType"],
        ["Groundwater"],
        "#1F77B4",
        ["Reservoir"],
        "#AEC7E8",
        ["Surface Water"],
        "#FF7F0E",
        ["Weather"],
        "#FFBB78",
        "black",
      ],
    },
    lreProperties: {
      popup: {
        titleField: "description",
        excludeFields: ["index", "description", "id"],
      },
    },
  };

  const locationsLabelsLayer = {
    id: "locations-labels",
    type: "symbol",
    source: "locations",
    minzoom: 7,
    layout: {
      "text-field": [
        "format",
        ["get", "description"],
        {}, // Use default formatting
        "\n",
        {},
        ["get", "name"],
        {
          "text-font": ["literal", ["DIN Offc Pro Italic"]],
          "font-scale": 0.85,
        },
      ],
      "text-size": 12,
      "text-offset": [0, -2.75],
      "text-font": ["literal", ["Roboto Black", "Arial Unicode MS Bold"]],
      visibility: "visible",
    },
    paint: {
      "text-color": "rgb(49,49,49)",
      "text-halo-color": "rgba(255,255,255,1)",
      "text-halo-width": 3,
    },
  };

  const boundaryLayer = {
    id: "cherry-creek-basin-boundary-line",
    name: "Cherry Creek Basin Boundary",
    type: "line",
    source: "cherry-creek-basin-boundary",
    "source-layer": "CC_Basin_Boundary-19nrzc",
    paint: {
      "line-color": "hsl(330,53%,53%)",
      "line-width": 3,
    },
  };

  const { data, isLoading, error } = useQuery(
    ["/api/public-map/wells2"],
    async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/wells`
        );
        return data.filter((location) => location.location_geometry);
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/" + DUMMY_BASEMAP_LAYERS[0].url,
      center: DEFAULT_MAP_CENTER,
      zoom: 9,
    });

    map.addControl(new mapboxgl.NavigationControl(), "top-left");
    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        // When active the map will receive updates to the device's location as it changes.
        trackUserLocation: true,
        // Draw an arrow next to the location dot to indicate which direction the device is heading.
        showUserHeading: true,
      }),
      "top-left"
    );
    map.addControl(new mapboxgl.FullscreenControl());
    // Add locate control to the map.
    map.addControl(new ResetZoomControl(), "top-left");

    DUMMY_BASEMAP_LAYERS.forEach((layer) => {
      return map.addControl(new ToggleBasemapControl(layer.url, layer.icon));
    });

    map.on("load", () => {
      setMapIsLoaded(true);
      setMap(map);
    });
  }, []); // eslint-disable-line

  //resizes map when mapContainerRef dimensions changes (sidebar toggle)
  useEffect(() => {
    if (map) {
      const resizer = new ResizeObserver(debounce(() => map.resize(), 100));
      resizer.observe(mapContainer.current);
      return () => {
        resizer.disconnect();
      };
    }
  }, [map]);

  useEffect(() => {
    if (mapIsLoaded && data?.length > 0 && typeof map != "undefined") {
      if (!map.getSource("locations")) {
        map.addSource("locations", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: data.map((location) => {
              return {
                id: location.locationid,
                type: "Feature",
                properties: {
                  name: location.location_name,
                  description: location.location_desc,
                  id: location.locationid,
                  index: location.locationid,
                  startDate: location.por_start,
                  endDate: location.por_end,
                  notes: location.notes,
                  locType: location.location_type,
                  parameterGroups:
                    location?.parameter_groups?.join(", ") || null,
                  huc8: location.huc_8,
                  huc12: location.huc_12,
                  county: location.county,
                },
                geometry: {
                  type: location.location_geometry.type,
                  coordinates: location.location_geometry.coordinates,
                },
              };
            }),
          },
        });
        // Add a layer showing the places.
        map.addSource("cherry-creek-basin-boundary", {
          type: "vector",
          url: "mapbox://ccbwqportal.9phxd44n",
        });
        // Add a layer showing the places.
        map.addLayer(boundaryLayer);
        map.addLayer(locationsLayer);
        map.addLayer(locationsLabelsLayer);

        map.setFilter("locations", [
          "in",
          ["get", "index"],
          ["literal", locations],
        ]);

        map.setFilter("locations-labels", [
          "in",
          ["get", "index"],
          ["literal", locations],
        ]);

        map.on("click", "locations", (e) => {
          map.fire("closeAllPopups");

          const features = map.queryRenderedFeatures(e.point);
          const myFeatures = features.filter(
            (feature) => feature.source === "locations"
          );
          const coordinates = [e.lngLat.lng, e.lngLat.lat];

          const popupNode = document.createElement("div");
          ReactDOM.render(
            //MJB adding style providers to the popup
            <StylesProvider jss={jss}>
              <MuiThemeProvider theme={theme}>
                <ThemeProvider theme={theme}>
                  <Popup
                    layers={[locationsLayer]}
                    features={myFeatures}
                    height="144px"
                    width="252px"
                    size="small"
                  />
                </ThemeProvider>
              </MuiThemeProvider>
            </StylesProvider>,
            popupNode
          );
          popUpRef.current
            .setLngLat(coordinates)
            .setDOMContent(popupNode)
            .addTo(map);
        });

        // Change the cursor to a pointer when the mouse is over the places layer.
        map.on("mouseenter", "locations", () => {
          map.getCanvas().style.cursor = "pointer";
        });

        // Change it back to a pointer when it leaves.
        map.on("mouseleave", "locations", () => {
          map.getCanvas().style.cursor = "";
        });
      }
    }
  }, [isLoading, mapIsLoaded, map, data]); //eslint-disable-line

  //filters the table based on the selected radioValues filters
  useEffect(() => {
    if (map !== undefined && map.getLayer("locations")) {
      map.setFilter("locations", [
        "in",
        ["get", "index"],
        ["literal", locations],
      ]);
      map.setFilter("locations-labels", [
        "in",
        ["get", "index"],
        ["literal", locations],
      ]);
    }
  }, [locations]); // eslint-disable-line

  const legendColors = [
    { name: `Groundwater`, color: `#1F77B4` },
    { name: `Reservoir`, color: `#AEC7E8` },
    { name: `Surface Water`, color: `#FF7F0E` },
    { name: `Weather`, color: `#FFBB78` },
  ];

  if (error) return "An error has occurred: " + error.message;

  return (
    <Root>
      <MapContainer ref={mapContainer} />
      {legendVisible && (
        <Legend subtitle="Location Types" legendColors={legendColors} />
      )}
      <LegendControl
        open={legendVisible}
        onToggle={() => setLegendVisible(!legendVisible)}
      />
    </Root>
  );
};

export default QueryAndDownloadMap;
